import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import { CAMERA_STATUS_COLOR, type ICamera } from '@interfaces/ICameras';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { AlertIcon, CloseIcon } from '@utils/icons';
import { CameraVideo } from '../CameraVideo/CameraVideo';
import styles from './CameraModal.module.css';

interface ICameraModalProps {
  camera: ICamera | null;
  onClose: () => void;
}

// Full-page viewer for a single camera 
export function CameraModal({ camera, onClose }: ICameraModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog.Root open={camera !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={styles.popup} aria-label={camera?.name}>
          {camera && (
            <>
              <header className={styles.header}>
                <div className={styles.heading}>
                  <span className={styles.badge} data-status={camera.status}>
                    <StatusDot color={CAMERA_STATUS_COLOR[camera.status]} />
                    {t(`cameras.status.${camera.status}`)}
                  </span>
                  <div className={styles.titles}>
                    <h2 className={styles.name}>{camera.name}</h2>
                    <span className={styles.meta}>
                      {camera.id} · {camera.floor} · {camera.zone}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.close}
                  onClick={onClose}
                  aria-label={t('cameras.modal.close')}
                >
                  <CloseIcon size={18} />
                </button>
              </header>

              <div className={styles.viewport} data-status={camera.status}>
                <CameraVideo
                  videoSrc={camera.videoSrc}
                  status={camera.status}
                  iconSize={64}
                  variant="modal"
                />
                {camera.status === 'alert' && camera.alertLabel && (
                  <span className={styles.alertPill}>
                    <AlertIcon size={16} />
                    {camera.alertLabel}
                  </span>
                )}
              </div>
            </>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
